import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Link } from 'react-router-dom';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamation } from '@fortawesome/free-solid-svg-icons';
import { Contest, NDAConfirm, User } from '../../../global';
import axiosHelper from '../../../helpers/axiosHelper';
import FormField from '../../Forms/FormField';
import { useForm } from 'react-hook-form';

interface NDAFormProps {
  contest: Contest;
  loggedUser: User | null;
  ndaWasSigned: () => void;
  loginCallback: () => void;
}

const NDAForm: React.FC<NDAFormProps> = ({ loggedUser, contest, ndaWasSigned, loginCallback }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NDAConfirm>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      firstName: loggedUser?.firstName || '',
      lastName: loggedUser?.lastName || '',
      acceptNDA: false,
    },
  });

  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (loggedUser) {
      console.log('loggedUser', loggedUser);
      setEmail(loggedUser.email || '');
    }
  }, [loggedUser]);

  const onSubmit = async (data: NDAConfirm) => {
    setErrorMessage('');
    if (!loggedUser) {
      loginCallback();
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await axiosHelper.sendNDAConfirm(data, contest);
      console.log('response', response);
      if (response.status === 200) {
        ndaWasSigned();
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        setErrorMessage('Your session has expired. Please log in again.');
      } else if (error.response?.status === 409) {
        setErrorMessage('The NDA could not be saved. It may already have been accepted. Please refresh and try again.');
      } else {
        setErrorMessage('The NDA could not be accepted. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="row mb-5">
        <div className="col-12 col-md-2 text fw-bold mb-3 ">
          <div className="d-flex flex-row justify-content-start align-items-center">
            <span className="me-2">NDA required</span>
            <div className="bg-danger d-flex align-items-center justify-content-center" style={{ borderRadius: '50%', height: '20px', width: '20px' }}>
              <FontAwesomeIcon icon={faExclamation as IconProp} className="text color-light" />
            </div>
          </div>
        </div>
        <div className="col-12 col-md-10">
          You must
          {loggedUser ? (
            <> </>
          ) : (
            <>
              {' '}
              <Link to={'#'} onClick={loginCallback}>
                login
              </Link>{' '}
              (or <Link to={`/signup`}>signup</Link>) and
            </>
          )}
          accept the contest NDA in order to read the full brief and join the contest or access the gallery. If you do not use your real name, your account may be suspended or
          terminated.
        </div>
      </div>
      <div className="row mb-5">
        <FormField name="firstName" label="First name" required error={errors.firstName}>
          <input
            type="text"
            id="firstName"
            placeholder="First name"
            autoComplete="given-name"
            {...register('firstName', { required: 'First name is required', minLength: 1, maxLength: 50 })}
          />
        </FormField>
      </div>
      <div className="row mb-5">
        <FormField name="lastName" label="Last name" required error={errors.lastName}>
          <input
            type="text"
            id="lastName"
            placeholder="Last name"
            autoComplete="family-name"
            {...register('lastName', { required: 'Last name is required', minLength: 1, maxLength: 50 })}
          />
        </FormField>
      </div>
      <div className="row mb-5">
        <FormField name="email" label="Email">
          <input type="text" value={email || ''} disabled></input>
        </FormField>
        <FormField name="acceptNDA" label="NDA" required error={errors.acceptNDA}>
          <input type="checkbox" id="checkbox-1" {...register('acceptNDA', { required: 'You must accept the User Agreement' })} className="" />
          <label htmlFor="checkbox-1" className="">
            I accept the&nbsp;
            <a href={`${contest.termsAttachmentURL}`} target="_blank" rel="noreferrer">
              contest NDA
            </a>
          </label>
        </FormField>
      </div>
      {errorMessage && (
        <div className="col-12 mt-3 color-danger" role="alert" aria-live="polite">
          {errorMessage}
        </div>
      )}
      <div className="row">
        <div className="col-12 d-flex justify-content-end">
          <button type="submit" className="btn-dark-blue confirm rounded" disabled={isSubmitting}>
            {isSubmitting ? 'saving…' : 'save'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default NDAForm;
